import type {
  HullPaintsPaint,
  HullPaintsTableMode,
  HullPaintsVessel,
} from "../models/hull-paints.models";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import { twMerge } from "tailwind-merge";

interface HullPaintsDataTableProps {
  mode: HullPaintsTableMode;
  vessels: HullPaintsVessel[];
  paints: HullPaintsPaint[];
  selectedImos: Set<string>;
  onToggleVessel: (imoNumber: string) => void;
}

const emptyValue = (value: string | null) => value ?? <span className="text-gray-300 dark:text-gray-600">—</span>;

const formatDate = (value: string | null): string | null => {
  const match = value?.match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (!match) {
    return value;
  }

  const [, year, month, day] = match;
  const monthName = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][Number(month) - 1];

  return monthName ? `${day}-${monthName}-${year.slice(-2)}` : value;
};

const attachmentLink = (value: string | null) => {
  if (!value || !/^https?:\/\//i.test(value)) {
    return emptyValue(null);
  }

  return (
    <a
      href={value}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center text-green hover:underline"
      aria-label="Open paint attachment"
    >
      <ArrowTopRightOnSquareIcon className="h-4 w-4" />
    </a>
  );
};

const HullPaintsDataTable = ({ mode, vessels, paints, selectedImos, onToggleVessel }: HullPaintsDataTableProps) => {
  if (mode === "paints") {
    return (
      <div className="max-h-[760px] overflow-auto" data-test="hull-paints-paint-table">
        <table className="w-full min-w-[1180px] border-separate border-spacing-0 text-[11px]">
          <thead className="sticky top-0 z-10 bg-gray-50 text-left text-[10px] uppercase tracking-wide text-gray-500 dark:bg-gray-900 dark:text-gray-400">
            <tr>
              {[
                "IMO", "Vessel name", "Desk", "Date of application", "Manufacture", "Boot top",
                "Vertical bottom", "Flat bottom", "Attachment name", "Attachment link",
              ].map((heading) => (
                <th key={heading} className="whitespace-nowrap border-b border-gray-200 px-3 py-3 font-semibold dark:border-gray-700">{heading}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paints.map((paint, index) => (
              <tr key={`${paint.imoNumber}-${paint.dateOfApplication ?? "unknown"}-${index}`} className="odd:bg-white even:bg-gray-50/60 dark:odd:bg-gray-800 dark:even:bg-gray-800/70">
                <td className="border-b border-gray-100 px-3 py-2 font-mono dark:border-gray-700">{paint.imoNumber}</td>
                <td className="border-b border-gray-100 px-3 py-2 font-semibold dark:border-gray-700">{paint.vesselName}</td>
                {[paint.desk, formatDate(paint.dateOfApplication), paint.manufacture, paint.bootTop, paint.verticalBottom, paint.flatBottom, paint.attachmentName].map((value, valueIndex) => (
                  <td key={valueIndex} className="whitespace-nowrap border-b border-gray-100 px-3 py-2 dark:border-gray-700">{emptyValue(value)}</td>
                ))}
                <td className="whitespace-nowrap border-b border-gray-100 px-3 py-2 dark:border-gray-700">{attachmentLink(paint.attachmentLink)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {paints.length === 0 && (
          <div className="p-10 text-center text-sm text-gray-500" data-test="hull-paints-empty-paints">
            Paint-library rows are not available from the current Snowflake endpoint; the Log New Paint action opens an external SharePoint destination.
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="max-h-[760px] overflow-auto" data-test="hull-paints-vessel-table">
      <table className="w-full min-w-[1380px] border-separate border-spacing-0 text-[11px]">
        <thead className="sticky top-0 z-10 bg-gray-50 text-left text-[10px] uppercase tracking-wide text-gray-500 dark:bg-gray-900 dark:text-gray-400">
          <tr>
            {[
              "Vessel name", "IMO", "Desk", "Built", "Yard", "Design", "Last request", "Info logged",
              "Contract type", "Delivery date", "Redelivery date",
            ].map((heading) => (
              <th key={heading} className="whitespace-nowrap border-b border-gray-200 px-3 py-3 font-semibold dark:border-gray-700">{heading}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {vessels.map((vessel) => {
            const selected = selectedImos.has(vessel.imoNumber);

            return (
              <tr
                key={vessel.imoNumber}
                className={twMerge(
                  "cursor-pointer text-gray-700 odd:bg-white even:bg-gray-50/60 hover:bg-primary-10 dark:text-gray-200 dark:odd:bg-gray-800 dark:even:bg-gray-800/70 dark:hover:bg-teal-950/50",
                  selected && "bg-primary-20 dark:bg-teal-950/70",
                )}
                onClick={() => onToggleVessel(vessel.imoNumber)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onToggleVessel(vessel.imoNumber);
                  }
                }}
                tabIndex={0}
                aria-selected={selected}
                data-test={`hull-paints-vessel-row-${vessel.imoNumber}`}
              >
                <td className="whitespace-nowrap border-b border-gray-100 px-3 py-2 font-semibold dark:border-gray-700">{vessel.vesselName}</td>
                <td className="whitespace-nowrap border-b border-gray-100 px-3 py-2 font-mono dark:border-gray-700">{vessel.imoNumber}</td>
                {[vessel.desk, formatDate(vessel.built), vessel.yard, vessel.design, formatDate(vessel.lastRequest), formatDate(vessel.infoLogged), vessel.contractType, formatDate(vessel.deliveryDate), formatDate(vessel.redeliveryDate)].map((value, index) => (
                  <td key={index} className="whitespace-nowrap border-b border-gray-100 px-3 py-2 dark:border-gray-700">{emptyValue(value)}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      {vessels.length === 0 && <div className="p-10 text-center text-sm text-gray-500">No vessels match the selected filters.</div>}
    </div>
  );
};

export default HullPaintsDataTable;
